import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { formatInTimeZone } from 'date-fns-tz';
import {
  useNavigate,
  useParams,
  useLocation,
} from 'react-router-dom-v5-compat';
import Wrapper from '../layout/Wrapper';
import { setFlowType } from '../redux/slices/formSlice';
import { FLOW_TYPES, URLS, VASS_PHONE_NUMBER } from '../utils/constants';
import { pathWithSearch } from '../utils/invitationQuery';
import { useGetAppointmentQuery } from '../redux/api/vassApi';
import { getBrowserTimezone } from '../utils/timezone';
import { isServerError } from '../utils/errors';

const AlreadyScheduled = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    data: appointmentData,
    isLoading,
    error: appointmentError,
  } = useGetAppointmentQuery({
    appointmentId,
  });

  const [appointmentDate, appointmentTime] = useMemo(() => {
    if (!appointmentData?.startUtc) {
      return ['', ''];
    }
    const date = new Date(appointmentData.startUtc);
    const timezone = getBrowserTimezone();
    return [
      formatInTimeZone(date, timezone, 'MM/dd/yyyy'),
      formatInTimeZone(date, timezone, 'hh:mm a'),
    ];
  }, [appointmentData?.startUtc]);

  const handleCancelAppointment = e => {
    e.preventDefault();
    dispatch(setFlowType(FLOW_TYPES.CANCEL));
    navigate(
      pathWithSearch(
        `${URLS.CANCEL_APPOINTMENT}/${appointmentData.appointmentId}`,
        location.search,
      ),
    );
  };

  return (
    <Wrapper
      testID="already-scheduled-page"
      pageTitle="You already scheduled your call with VA Solid Start"
      loading={isLoading}
      loadingMessage="Loading appointment details. This may take up to 30 seconds. Please don’t refresh the page."
      errorAlert={isServerError(appointmentError)}
    >
      <p
        id="appointment-date-time"
        data-dd-privacy="mask"
        data-testid="already-scheduled-date-time"
      >
        Your VA Solid Start call is scheduled for {appointmentDate} at{' '}
        {appointmentTime}.
      </p>
      <p data-testid="already-scheduled-phone-number">
        Your VA Solid Start representative will call you at the time you
        requested from <va-telephone contact={VASS_PHONE_NUMBER} />. Save this
        number to ensure you don’t miss the call.
      </p>
      <p>If you need to, you can cancel your call with VASS online.</p>
      <va-link-action
        href={`${URLS.CANCEL_APPOINTMENT}/${appointmentData?.appointmentId}`}
        text="Cancel your VASS call"
        aria-labelledby="appointment-date-time"
        type="secondary"
        data-testid="already-scheduled-cancel-button"
        onClick={handleCancelAppointment}
      />
      <p data-testid="already-scheduled-reschedule-message">
        If you want to reschedule this appointment, call us at{' '}
        <va-telephone contact={VASS_PHONE_NUMBER} />. We’re here Monday through
        Friday, 8:00 a.m. to 9:00 p.m. ET.
      </p>
    </Wrapper>
  );
};

export default AlreadyScheduled;
